import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import BookingGetters from "../../store/onlineBooking/getters";
import { IoInformationCircle } from "react-icons/io5";

type Props = {
  onSelect: (page: string) => void;
  isSmallScreen: boolean;
  onBack: () => void;
};

export default function WebCheckInHome({
  onSelect,
  isSmallScreen,
  onBack,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinHeaderBgColor = appSettings?.webCheckinHeaderBgColor;
  const webCheckinHeaderTextColor = appSettings?.webCheckinHeaderTextColor;
  const webCheckinContentBgColor = appSettings?.webCheckinContentBgColor;
  const webCheckinContentTextColor = appSettings?.webCheckinContentTextColor;
  const webCheckinButtonColor = appSettings?.webCheckinButtonColor;
  const webCheckinButtonTextColor = appSettings?.webCheckinButtonTextColor;
  const webCheckinPrimaryColor = appSettings?.webCheckinPrimaryColor;
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;

  const boxStyles = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    height: "51px",
    marginTop: "20px",
    marginBottom: "20px",
    paddingLeft: isSmallScreen ? "22px" : "40px",
  };

  const registrationStyles = {
    color: webCheckinContentTextColor || "#868686",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    fontSize: isSmallScreen ? "11px" : "14px",
    lineHeight: isSmallScreen ? "107%" : "111%",
    letterSpacing: "0%",
    textAlign: "left",
  };

  const typographyStyles = {
    mt: 1,
    fontSize: isSmallScreen ? "19px" : "22px",
    color: webCheckinHeaderBgColor || "#2CB133",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    lineHeight: "100%",
    letterSpacing: "0%",
    marginTop: "20px",
  };

  const getButtonStyles = (
    webCheckinButtonColor?: string,
    webCheckinButtonTextColor?: string
  ) => ({
    backgroundColor: webCheckinButtonColor || "#19344F",
    color: webCheckinButtonTextColor || "#FFFFFF",
    borderRadius: `${appSettings?.buttonBorderRadius || 86}px`,
    width: { xs: "280px", md: "360px" },
    height: { xs: "54px", md: "64px" },
    fontSize: { xs: "15px", md: "20px" },
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
    lineHeight: "100%",
    textTransform: "uppercase",
    letterSpacing: "0%",
    "&:hover": {
      backgroundColor: webCheckinButtonColor || "#19344F",
      color: webCheckinButtonTextColor || "#FFFFFF",
    },
  });

  const buttonStyles = useMemo(
    () => getButtonStyles(webCheckinButtonColor, webCheckinButtonTextColor),
    [webCheckinButtonColor, webCheckinButtonTextColor]
  );

  type SearchOptionProps = {
    label: string;
    example: string;
    info: string;
    onClick: () => void;
    buttonStyles: any;
    typographyStyles: any;
    registrationStyles: any;
    boxStyles: any;
  };
  const IoInformation = IoInformationCircle as React.ElementType;

  const SearchOption = ({
    label,
    example,
    info,
    onClick,
    buttonStyles,
    typographyStyles,
    registrationStyles,
    boxStyles,
  }: SearchOptionProps) => (
    <>
      <Button variant="contained" sx={buttonStyles} onClick={onClick}>
        {label}
      </Button>
      <Typography sx={typographyStyles}>
        e.g. <span style={{ fontWeight: 700 }}>{example}</span>
      </Typography>
      <Box sx={boxStyles} className="mx-1">
        <IoInformation
          style={{
            width: 20,
            height: 20,
            color: webCheckinPrimaryColor
              ? webCheckinPrimaryColor
              : webCheckinSecondaryColor || "#E3007B",
            flexShrink: 0,
            lineHeight: 1,
          }}
        />

        <Typography sx={registrationStyles}>{info}</Typography>
      </Box>
    </>
  );

  return (
    <Card
      className="webCheckInCard"
      sx={{
        textAlign: "center",
        backgroundColor: webCheckinContentBgColor || "#F4F4F4",
      }}
    >
      <Box
        className="webCheckInBox"
        sx={{
          backgroundColor: webCheckinHeaderBgColor || "#2cb133",
        }}
      >
        <IconButton
          onClick={onBack}
          className="webCheckInIconButton"
          sx={{
            color: webCheckinHeaderTextColor || "#fff",
          }}
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography
          variant="h6"
          className="webCheckInHeader"
          sx={{
            color: webCheckinHeaderTextColor || "#fff",
          }}
        >
          WEB CHECK IN
        </Typography>
      </Box>
      <CardContent
        sx={{
          padding: "25px",
          paddingLeft: isSmallScreen ? "10px" : "60px",
          paddingRight: isSmallScreen ? "10px" : "60px",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            fontWeight: "bold",
            fontFamily: "Inter, sans-serif",
            fontSize: isSmallScreen ? "20px" : "24px",
            marginBottom: isSmallScreen ? "20px" : "35px",
            color: webCheckinContentTextColor || "#19344F",
          }}
        >
          {translate("Search your Reservation by:")}
        </Typography>

        <SearchOption
          label={translate("Reservation Number")}
          example="XYZ - 00000"
          info={translate("reservation.info")}
          onClick={() => onSelect("SearchByReservationNumber")}
          {...{ buttonStyles, typographyStyles, registrationStyles, boxStyles }}
        />

        <Divider className="webCheckInDivider" />

        <SearchOption
          label={translate("Voucher Number")}
          example="XY 00 X0X0X0X"
          info={translate("voucher.info")}
          onClick={() => onSelect("SearchByVoucherNumber")}
          {...{ buttonStyles, typographyStyles, registrationStyles, boxStyles }}
        />
      </CardContent>
    </Card>
  );
}
